import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const LINES = [
  { text: "YOUR CUT.", accent: false },
  { text: "YOUR BARBER.", accent: false },
  { text: "YOUR STYLE.", accent: true },
];

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 180]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#0c1220]"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.12, opacity: 0.5 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: EASE }}
          src="/assets/hero.jpg"
          alt="Inside Phade Barbers — LED honeycomb ceiling above the cutting floor in Calhoun, Georgia"
          fetchpriority="high"
          className="h-full w-full object-cover object-top [filter:contrast(1.05)_saturate(0.95)]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0c1220]/55 via-[#0c1220]/70 to-[#0c1220]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0c1220]/75 via-[#0c1220]/20 to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-[1240px] px-5 pb-24 pt-36 sm:px-8 sm:pt-40">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.7, ease: EASE }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.04] px-4 py-2 backdrop-blur-sm"
          data-testid="hero-location-badge"
        >
          <MapPin className="h-3.5 w-3.5 text-[#9fd3f0]" />
          <span className="text-[11px] font-semibold tracking-[0.22em] text-white/85">
            CALHOUN, GEORGIA
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="text-[13.5vw] font-black uppercase leading-[0.95] tracking-[-0.02em] text-white sm:text-7xl lg:text-8xl"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "text-[#9fd3f0]" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.13, duration: 1, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subcopy"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
          className="mt-7 max-w-[560px] text-base leading-relaxed text-white/70 sm:text-lg"
        >
          Modern cuts, clean fades, beard work and more — right here in Calhoun.
          Choose your barber and book your next cut.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-choose-barber-button"
            onClick={() => scrollToId("barbers")}
            className="group inline-flex items-center gap-2.5 rounded-lg bg-[#4a9bdc] px-8 py-4 text-sm font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#5aaae8]"
          >
            CHOOSE YOUR BARBER
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-view-services-button"
            onClick={() => scrollToId("services")}
            className="inline-flex items-center rounded-lg border border-white/25 px-8 py-4 text-sm font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:border-white/50 hover:bg-white/[0.06]"
          >
            VIEW SERVICES
          </button>
        </motion.div>
      </div>
    </section>
  );
};
